<template>
  <div class="bg-white rounded-xl w-full h-full min-h-0 overflow-y-auto flex-col flex-start pe-2 mb-4 pb-6">
    <div class="flex flex-col gap-3 text-sm">
      <div class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">תאריך ביצוע</span>
        <span v-if="payment.chargedDate" class="text-left">{{ DateTime.fromISO(payment.chargedDate as string).toLocaleString(DateTime.DATETIME_MED) }}</span>
        <span v-else-if="payment.createdAt" class="text-left">{{ DateTime.fromISO(payment.createdAt as string).toLocaleString(DateTime.DATETIME_MED) }}</span>
      </div>
      <div class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">אמצעי תשלום</span>
        <span class="text-left">{{ coveredByBalance ? 'יתרת לקוח' : paymentMethodsNames[payment.paymentMethod] }}</span>
      </div>
      <div class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">מספר עסקה</span>
        <span class="text-left">{{ payment.transactionNumber ?? 'אין' }}</span>
      </div>
      <!-- How the amount came to be, when anything was taken off the price -->
      <div v-if="breakdown.length" class="flex flex-col gap-2 rounded-xl bg-gray-50 border border-gray-100 p-3">
        <div class="flex justify-between w-full">
          <span class="text-gray-500">מחיר</span>
          <span class="text-left">{{ ils(payment.paymentSplit!.subtotal) }}</span>
        </div>
        <div v-for="line in breakdown" :key="line.label" class="flex justify-between w-full">
          <span class="text-gray-500">{{ line.label }}</span>
          <span class="text-left text-green-700" style="direction: ltr">−{{ ils(line.amount) }}</span>
        </div>
        <div class="flex justify-between w-full border-t border-gray-200 pt-2 font-medium">
          <span>{{ coveredByBalance ? 'לתשלום' : 'שולם' }}</span>
          <span class="text-left">{{ ils(paidAmount) }}</span>
        </div>
        <span v-if="coveredByBalance" class="text-xs text-gray-500">
          <i class="pi pi-info-circle ml-1 !text-xs" />התשלום כוסה במלואו מיתרת הלקוח, ללא חיוב.
        </span>
      </div>
      <div v-else class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">סכום</span>
        <span class="text-left">{{ ils(paidAmount) }}</span>
      </div>
      <div v-if="refundedAmount > 0" class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">הוחזר</span>
        <span class="text-left text-orange-500">₪{{ refundedAmount }}</span>
      </div>
      <div class="flex justify-between w-full">
        <span class="text-gray-500 w-1/2">סטטוס</span>
        <span class="text-left flex gap-1 items-center px-2 rounded-lg" :class="statusClass">
          <span>{{ statusLabel }}</span>
          <i class="pi" :class="statusIcon" style="font-size: 0.75rem" />
        </span>
      </div>
    </div>

    <!-- Refund history -->
    <div v-if="payment.refunds?.length" class="mt-4 border-t pt-3 flex flex-col gap-2">
      <span class="text-sm font-medium text-gray-700">היסטוריית החזרים</span>
      <div v-for="(refund, idx) of payment.refunds" :key="idx" class="flex justify-between items-start text-xs text-gray-600">
        <div class="flex flex-col">
          <span>₪{{ refund.amount }}{{ refund.manual ? ' (ידני)' : '' }}</span>
          <span v-if="refund.reason" class="text-gray-400">{{ refund.reason }}</span>
        </div>
        <span class="text-gray-400">{{ DateTime.fromISO(refund.refundedAt as string).toLocaleString(DateTime.DATE_MED) }}</span>
      </div>
    </div>

    <!-- Refund action -->
    <div v-if="canRefund" class="mt-4 border-t pt-4 flex flex-col gap-3">
      <div v-if="!showRefundForm">
        <Button
          outlined
          severity="danger"
          class="w-full"
          icon="pi pi-replay"
          label="החזר כספי"
          @click="showRefundForm = true"
        />
      </div>
      <div v-else class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm text-gray-700">סכום ההחזר (עד ₪{{ remaining }})</label>
          <InputNumber
            v-model="refundAmount"
            :min="0"
            :max="remaining"
            :maxFractionDigits="2"
            mode="currency"
            currency="ILS"
            locale="he-IL"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm text-gray-700">סיבה (אופציונלי)</label>
          <InputText v-model="refundReason" class="w-full" placeholder="לדוגמה: ביטול תור" />
        </div>
        <div v-if="isManualOnly" class="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-200">
          <i class="pi pi-info-circle ml-1" />
          תשלום זה אינו נתמך בהחזר אוטומטי דרך הסליקה. ההחזר יירשם במערכת בלבד — יש לבצע את ההחזר בפועל בממשק הסליקה.
        </div>
        <div class="flex gap-2">
          <Button
            severity="danger"
            class="flex-1"
            :loading="isRefunding"
            :label="isManualOnly ? 'רישום החזר' : 'בצע החזר'"
            @click="doRefund"
          />
          <Button outlined class="flex-1" label="ביטול" :disabled="isRefunding" @click="showRefundForm = false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { IPaymentsResponse, PaymentMethod, PaymentProvider, PaymentRequestStatus } from '@torim-app/types';
import { DateTime } from 'luxon';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import { useToast } from 'vue-toastification';
import { PaymentsApi } from '@app/api/admin/payments-api';
import { useAdminStore } from '@app/stores';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps({
  selectedPayment: {
    type: Object as PropType<IPaymentsResponse>,
    required: true
  },
  onRefunded: {
    type: Function as PropType<(p: IPaymentsResponse) => void>,
    required: false
  }
});

const toast = useToast();
const { closeDrawer } = useDrawer();
const adminStore = useAdminStore();

// Refunds are only supported for PayMe businesses (automatic via the gateway, or record-only).
// Other providers (Hyp/Tranzila/…) have no refund integration, so hide the action entirely.
const isPaymeBusiness = computed(() =>
  adminStore.getBusinessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME
);

// Local copy so the UI updates immediately after a refund
const payment = ref<IPaymentsResponse>({ ...props.selectedPayment });

const showRefundForm = ref(false);
const isRefunding = ref(false);
const refundReason = ref('');

const refundedAmount = computed(() => payment.value.refundedAmount ?? 0);

const ils = (n?: number) => '₪' + (n ?? 0).toLocaleString('he-IL', { maximumFractionDigits: 2 });

// Everything taken off the price (each paid by something other than this charge)
const breakdown = computed<Array<{ label: string; amount: number }>>(() => {
  const split = payment.value.paymentSplit;
  if (!split) return [];
  return [
    { label: 'קיזוז מיתרת הלקוח', amount: split.balanceDiscount?.amount ?? 0 },
    { label: 'שובר', amount: split.voucherDiscount?.amount ?? 0 },
    { label: split.couponDiscount?.code ? `קופון (${split.couponDiscount.code})` : 'קופון', amount: split.couponDiscount?.amount ?? 0 },
    { label: 'הטבת מועדון לקוחות', amount: split.loyaltyDiscount?.amount ?? 0 },
    { label: 'כרטיסייה', amount: split.bundlePackDiscount?.amount ?? 0 },
  ].filter(line => line.amount > 0);
});

// Nothing charged — the customer's credit paid it all (recorded as a ₪0 non-card payment)
const coveredByBalance = computed(() =>
  paidAmount.value <= 0 && (payment.value.paymentSplit?.balanceDiscount?.amount ?? 0) > 0);
const paidAmount = computed(() => payment.value.paidPrice ?? payment.value.price ?? 0);
const remaining = computed(() => Math.round((paidAmount.value - refundedAmount.value) * 100) / 100);
const refundAmount = ref(remaining.value);

// Default the amount to what's still refundable each time the form opens, so a
// second refund after a partial one starts from the remaining balance (full price
// minus what was already refunded), not the original total.
watch(showRefundForm, (open) => {
  if (open) refundAmount.value = remaining.value;
});

// Cash / bank-transfer can't be refunded through the gateway — record-only
const isManualOnly = computed(() =>
  ![PaymentMethod.Credit, PaymentMethod.Bit].includes(payment.value.paymentMethod) || !payment.value.transactionNumber
);

const canRefund = computed(() =>
  isPaymeBusiness.value &&
  [PaymentRequestStatus.Success, PaymentRequestStatus.PartiallyRefunded].includes(payment.value.status) &&
  remaining.value > 0 &&
  !!payment.value.uuid
);

const statusLabel = computed(() => {
  switch (payment.value.status) {
    case PaymentRequestStatus.Refunded: return 'הוחזר';
    case PaymentRequestStatus.PartiallyRefunded: return 'הוחזר חלקית';
    default: return 'שולם';
  }
});
const statusClass = computed(() => {
  switch (payment.value.status) {
    case PaymentRequestStatus.Refunded: return 'text-orange-500 bg-orange-50';
    case PaymentRequestStatus.PartiallyRefunded: return 'text-orange-500 bg-orange-50';
    default: return 'text-green-500 bg-green-50';
  }
});
const statusIcon = computed(() =>
  payment.value.status === PaymentRequestStatus.Success ? 'pi-check' : 'pi-replay'
);

const doRefund = async () => {
  if (!payment.value.uuid) return;
  if (!refundAmount.value || refundAmount.value <= 0 || refundAmount.value > remaining.value) {
    toast.error('יש להזין סכום החזר תקין');
    return;
  }
  try {
    isRefunding.value = true;
    const result = await PaymentsApi.refundPayment({
      paymentRequestUuid: payment.value.uuid,
      amount: refundAmount.value,
      reason: refundReason.value || undefined,
      manual: isManualOnly.value
    });
    trackEvent('payment_refunded', { amount: refundAmount.value, manual: isManualOnly.value });

    payment.value.status = result.status;
    payment.value.refundedAmount = result.refundedAmount;
    payment.value.refunds = [...(payment.value.refunds ?? []), result.refund];

    toast.success('ההחזר בוצע בהצלחה');
    showRefundForm.value = false;
    props.onRefunded?.(payment.value);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לבצע את ההחזר');
  } finally {
    isRefunding.value = false;
  }
};

const paymentMethodsNames: Record<string, string> = {
  'apple-pay': 'Apple Pay',
  'bank-transfer': 'העברה בנקאית',
  'google-pay': 'Google Pay',
  'pay-box': 'Paybox',
  bit: 'ביט',
  cash: 'מזומן',
  credit: 'כרטיס אשראי',
  'credit-card': 'כרטיס אשראי'
};
</script>
